import {
  Icon,
  RouteButtonLink,
} from "@equinor/e-procurement-portal-and-ts-portal-front-end-components";
import { mdiEye, mdiInformation, mdiScriptTextPlay } from "@mdi/js";
import { TabContext, TabPanel } from "@mui/lab";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  IconButton,
  Link,
  Paper,
  Skeleton,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableFooter,
  TableHead,
  TablePagination,
  TableRow,
  Tabs,
  Tooltip,
  Typography,
} from "@mui/material";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import type { MaintenanceWindow } from "../../../../@types/services/MaintenanceWindow";
import { getQuotationsForIssuePurchaseOrder } from "../../../../__generated__/orval/endpoints/quotations/quotations";
import type { EntitiesQuotationCollectionMain } from "../../../../__generated__/orval/models/entitiesQuotationCollectionMain.zod";
import {
  createCreatePurchaseOrderByQuotation,
  listCreatePurchaseOrderByQuotations,
} from "../../../../services/createPurchaseOrderByQuotations";
import { getMicrosoftToken } from "../../../../services/getMicrosoftToken";
import { fetchMaintenanceWindows } from "../../../../services/maintenanceWindows";
import { i18nextParseToDate } from "../../../../utils/i18nextParseTo";
import { PurchaseOrderPreviewModal } from "./-internal_components/PurchaseOrderPreviewModal";

type DataTabProps = {
  valid: boolean;
  selected: number[];
  selectedCallback: React.Dispatch<React.SetStateAction<number[]>>;
};

const DataTab: React.FC<DataTabProps> = ({
  valid,
  selected,
  selectedCallback,
}) => {
  const { t } = useTranslation();

  const [totalRows, setTotalRows] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [quotations, setQuotations] = useState<
    EntitiesQuotationCollectionMain[]
  >([]);
  const [loading, setLoading] = useState(true);
  const maximumSelectedItems = 100;

  const handleFetchQuotations = useCallback(
    async (page: number) => {
      setLoading(() => true);
      await getQuotationsForIssuePurchaseOrder({
        items_per_page: rowsPerPage,
        order: "desc",
        page,
        valid,
      })
        .then((quotation) => {
          setQuotations(() => quotation.results || []);
          setTotalRows(() => quotation.total_items || 0);
        })
        .finally(() => {
          setLoading(() => false);
        });
    },
    [rowsPerPage, valid],
  );

  const handleChangePage = (
    _event: React.MouseEvent<HTMLButtonElement, MouseEvent> | null,
    value: number,
  ): void => {
    setCurrentPage(() => value + 1);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>,
  ): void => {
    setRowsPerPage(() => parseInt(event.target.value, 10));
    setCurrentPage(() => 1);
  };

  const handleSelectAllClick = (
    event: React.ChangeEvent<HTMLInputElement>,
  ): void => {
    if (event.target.checked) {
      let newSelected: number[] = [];
      newSelected = newSelected.concat(selected);

      quotations.forEach((quotation) => {
        if (
          newSelected.length < maximumSelectedItems &&
          !newSelected.includes(quotation.id)
        ) {
          newSelected.push(quotation.id);
        }
      });

      selectedCallback(() => newSelected);
      return;
    }

    selectedCallback(() =>
      selected.filter(
        (el) => !quotations.map((quotation) => quotation.id).includes(el),
      ),
    );
  };

  const handleSelectClick = (
    _event: React.MouseEvent<HTMLTableRowElement, MouseEvent>,
    id: number,
  ): void => {
    const selectedIndex = selected.indexOf(id);
    let newSelected: number[] = [];

    if (selectedIndex === -1) {
      if (selected.length >= maximumSelectedItems) {
        newSelected = selected;
      } else {
        newSelected = newSelected.concat(selected, id);
      }
    } else if (selectedIndex === 0) {
      newSelected = newSelected.concat(selected.slice(1));
    } else if (selectedIndex === selected.length - 1) {
      newSelected = newSelected.concat(selected.slice(0, -1));
    } else if (selectedIndex > 0) {
      newSelected = newSelected.concat(
        selected.slice(0, selectedIndex),
        selected.slice(selectedIndex + 1),
      );
    }
    selectedCallback(() => newSelected);
  };

  const isTableRowSelected = (id: number): boolean => {
    return selected.indexOf(id) !== -1;
  };

  useEffect(() => {
    handleFetchQuotations(currentPage);
  }, [currentPage, handleFetchQuotations]);

  return (
    <TableContainer component={Paper}>
      <Table sx={{ minWidth: 650 }}>
        <TableHead>
          <TableRow>
            <TableCell padding="checkbox">
              <Checkbox
                checked={
                  quotations.length > 0 && selected.length === quotations.length
                }
                color="primary"
                indeterminate={
                  selected.length > 0 && selected.length !== quotations.length
                }
                onChange={handleSelectAllClick}
              />
            </TableCell>
            <TableCell align="left">{t("quotation.identifier")}</TableCell>
            <TableCell align="left">{t("quotation.requisitionDate")}</TableCell>
            <TableCell align="left">{t("quotation.material")}</TableCell>
            <TableCell align="left">{t("quotation.vendor")}</TableCell>
            <TableCell align="center" />
          </TableRow>
        </TableHead>
        <TableBody>
          {!!loading && (
            <TableRow key="loading-skeleton">
              {Array(6).map(() => (
                <TableCell key={`skeleton-cell-${crypto.randomUUID()}`}>
                  <Skeleton />
                </TableCell>
              ))}
            </TableRow>
          )}
          {!loading &&
            quotations?.length > 0 &&
            quotations.map((quotation) => (
              <TableRow
                aria-checked={isTableRowSelected(quotation.id)}
                hover
                key={quotation.id}
                onClick={(
                  event: React.MouseEvent<HTMLTableRowElement, MouseEvent>,
                ) => handleSelectClick(event, quotation.id)}
                role="checkbox"
                selected={isTableRowSelected(quotation.id)}
                sx={{ cursor: "pointer" }}
                tabIndex={-1}
              >
                <TableCell padding="checkbox">
                  <Checkbox
                    checked={isTableRowSelected(quotation.id)}
                    color="primary"
                  />
                </TableCell>
                <TableCell>{quotation.identifier}</TableCell>
                <TableCell>
                  {!!quotation.pr_item.requisition_date &&
                    t(
                      "intlDate",
                      i18nextParseToDate(quotation.pr_item.requisition_date),
                    )}
                </TableCell>
                <TableCell>{quotation.material.matnumber}</TableCell>
                <TableCell>{quotation.vendor_winner?.name}</TableCell>

                <TableCell align="center">
                  <Tooltip title={t("view")}>
                    <IconButton
                      component={Link}
                      // to={`/vbuyer/quotations/${quotation.id}`}
                      href={`/vbuyer/quotations/${quotation.id}`}
                      target="_blank"
                    >
                      <Icon icon={mdiEye} />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
        </TableBody>
        <TableFooter>
          <TableRow>
            <TableCell colSpan={2}>
              <Box sx={{ alignItems: "center", display: "flex" }}>
                <Box
                  sx={{
                    alignItems: "center",
                    display: "flex",
                    paddingRight: 1,
                  }}
                >
                  <Tooltip
                    title={t("issuePurchaseOrder.maximumSelectedItems", {
                      count: maximumSelectedItems,
                    })}
                  >
                    <Icon icon={mdiInformation} />
                  </Tooltip>
                </Box>
                <Typography>
                  {t("rowSelected", { count: selected.length })}
                </Typography>
              </Box>
            </TableCell>
            <TablePagination
              count={totalRows}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              page={currentPage - 1}
              rowsPerPage={rowsPerPage}
              rowsPerPageOptions={[5, 10, 25, 50, 100]}
              showFirstButton
              showLastButton
            />
          </TableRow>
        </TableFooter>
      </Table>
    </TableContainer>
  );
};

const IssuingPurchaseOrder: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [maintenanceWindows, setMaintenanceWindows] = useState<
    MaintenanceWindow[]
  >([]);
  const [selected, setSelected] = useState<number[]>([]);
  const [loadingGeneratePurchaseOrder, setLoadingGeneratePurchaseOrder] =
    useState(false);
  const [isPreviewModalOpen, setIsPreviewModalOpen] = useState(false);
  const [groupErrors, setGroupErrors] = useState<(string | null)[] | undefined>(
    undefined,
  );
  const [tabOpen, setTabOpen] = useState("valid");
  const [hasIncompleteOrders, setHasIncompleteOrders] = useState(false);

  useEffect(() => {
    const checkIncompleteOrders = async (): Promise<void> => {
      try {
        const response = await listCreatePurchaseOrderByQuotations({
          filterCompleted: false,
          itemsPerPage: 1,
          order: "desc",
          page: 1,
        });

        if (response.results && response.results.length > 0) {
          setHasIncompleteOrders(true);
        }
      } catch (error) {
        console.error("Error checking incomplete orders:", error);
      }
    };

    checkIncompleteOrders();
  }, []);

  const didInit = useRef(false);

  const hasMaintenanceWindows = maintenanceWindows.length > 0;

  const handleGeneratePurchaseOrder = (): void => {
    if (hasMaintenanceWindows) {
      return;
    }
    setGroupErrors(undefined);
    setIsPreviewModalOpen(true);
  };

  const handleConfirmGenerate = async (groups: number[][]): Promise<void> => {
    setLoadingGeneratePurchaseOrder(true);
    setGroupErrors(undefined);

    try {
      const authenticated = await getMicrosoftToken("procurement");
      if (!authenticated) {
        return;
      }

      const createResults = await Promise.allSettled(
        groups.map((ids) => createCreatePurchaseOrderByQuotation(ids)),
      );

      const errors = createResults.map((r) =>
        r.status === "rejected"
          ? ((r.reason as { response?: { data?: { error?: string } } })
              ?.response?.data?.error ?? "Unknown error")
          : null,
      );

      if (errors.some((e) => e != null)) {
        setGroupErrors(errors);
        return;
      }

      setIsPreviewModalOpen(false);
      navigate({ to: "/vbuyer/create_purchase_order_by_quotations" });
    } finally {
      setLoadingGeneratePurchaseOrder(false);
    }
  };

  const handleTabChange = (
    _event: React.SyntheticEvent,
    newValue: string,
  ): void => {
    setTabOpen(() => newValue);
  };

  const handleFetchMaintenanceWindows = useCallback(async () => {
    const response = await fetchMaintenanceWindows({
      filters: { active: true, system: "sap_p03" },
    });
    setMaintenanceWindows(
      response.results.filter(
        (maintenanceWindow) =>
          maintenanceWindow.blocked_actions.length === 0 ||
          maintenanceWindow.blocked_actions.includes(
            "issue_material_purchase_order",
          ),
      ),
    );
  }, []);

  useEffect(() => {
    if (!didInit.current) {
      didInit.current = true;
      handleFetchMaintenanceWindows();
    }
  }, [handleFetchMaintenanceWindows]);

  return (
    <Box
      component="main"
      sx={{
        display: "flex",
        flexDirection: "column",
        flexGrow: 1,
        height: "100%",
        justifyContent: "center",
        px: 2,
        width: "100%",
      }}
    >
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
        }}
      >
        <h1>{t("issuePurchaseOrder.title")}</h1>

        <Box
          sx={{
            alignItems: "center",
            display: "flex",
          }}
        >
          <RouteButtonLink
            startIcon={<Icon icon={mdiScriptTextPlay} />}
            to="/vbuyer/create_purchase_order_by_quotations"
            variant="outlined"
          >
            <span>{t("issuePurchaseOrder.trackExecutions")}</span>
          </RouteButtonLink>
        </Box>
      </Box>

      {hasMaintenanceWindows &&
        maintenanceWindows.map((maintenanceWindow) => (
          <Alert key={maintenanceWindow.id} severity="warning" sx={{ mb: 2 }}>
            {maintenanceWindow.message}
          </Alert>
        ))}

      {!!hasIncompleteOrders && (
        <Alert severity="error" sx={{ mb: 2 }}>
          <Link
            color="inherit"
            href="/vbuyer/create_purchase_order_by_quotations"
            rel="noreferrer"
            target="_blank"
            underline="hover"
          >
            {t("createPurchaseOrderByQuotation.incompleteOrdersError")}
          </Link>
        </Alert>
      )}

      {loadingGeneratePurchaseOrder ? (
        <Button color="info" variant="outlined">
          <CircularProgress
            color="success"
            size={16}
            sx={{ marginRight: "5px" }}
          />
          <span>{t("issuePurchaseOrder.generatingPurchaseOrder")}</span>
        </Button>
      ) : (
        <Button
          disabled={selected.length <= 0 || hasMaintenanceWindows}
          onClick={handleGeneratePurchaseOrder}
          type="button"
          variant="outlined"
        >
          {t("issuePurchaseOrder.generatePurchaseOrder")}
        </Button>
      )}

      <PurchaseOrderPreviewModal
        confirmLoading={loadingGeneratePurchaseOrder}
        groupErrors={groupErrors}
        onClose={() => {
          setIsPreviewModalOpen(false);
          setGroupErrors(undefined);
        }}
        onConfirm={handleConfirmGenerate}
        open={isPreviewModalOpen}
        quotationIds={selected}
      />

      <TabContext value={tabOpen}>
        <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
          <Tabs onChange={handleTabChange} value={tabOpen}>
            <Tab
              label={t("issuePurchaseOrder.quotationsValid")}
              value="valid"
            />
            <Tab
              label={t("issuePurchaseOrder.quotationsInvalid")}
              value="invalid"
            />
          </Tabs>
        </Box>

        <TabPanel value="valid">
          <DataTab selected={selected} selectedCallback={setSelected} valid />
        </TabPanel>
        <TabPanel value="invalid">
          <DataTab
            selected={selected}
            selectedCallback={setSelected}
            valid={false}
          />
        </TabPanel>
      </TabContext>
    </Box>
  );
};

export const Route = createFileRoute(
  "/_private/vbuyer/quotations/issue_purchase_order",
)({
  component: IssuingPurchaseOrder,
});
